import React, { useMemo } from 'react'
import T from 'prop-types'
import { Box, Divider, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import ChoiceRecap from 'components/atoms/ChoiceRecap'
import FilterSelect from 'components/atoms/FilterSelect'

import {
  FILTER_ASSIGNMENT_CAPS_BLIND_OFFERS,
  FILTER_ASSIGNMENT_CAPS_BLIND_NEGATIVE_OFFERS,
  FILTER_ASSIGNMENT_CAPS_BLIND_POSITIVE_OFFERS,
  FILTER_ASSIGNMENT_CAPS_FIRST_CHOICE,
  FILTER_ASSIGNMENT_CAPS_NONE_CHOICE,
  FILTER_ASSIGNMENT_CAPS_NOT_CHOSEN,
  FILTER_ASSIGNMENT_TYPE_AUTOMATIC_CHOICE,
  FILTER_ASSIGNMENT_TYPE_NONE_CHOICE,
  FILTER_ASSIGNMENT_TYPE_NO_PROPOSAL_CHOICE,
  FILTER_ASSIGNMENT_TYPE_TEAM_PROPOSAL_CHOICE,
} from 'hooks/useCarsFilters'

import { getAssignmentRecapValues } from '../Assignment/assignmentLib'

export default function AssignmentChoicesRecap({
  cars = [],
  filters,
  filtersData,
  setAssignmentPartners,
  setFilteringCaps,
  setAssignmentType,
  showAppealing = true,
}) {
  const { t } = useTranslation()

  const choices = useMemo(
    () =>
      getAssignmentRecapValues(
        cars,
        filters.assignmentPartners !== t('common.form.all')
          ? filters.assignmentPartners
          : undefined
      ),
    [cars, filters.assignmentPartners, t]
  )

  return (
    <>
      <Stack
        direction="row"
        flexWrap="wrap"
        data-testid="assignment-choice-recap"
      >
        <FilterSelect
          allOption={{ label: t('common.form.all'), value: '' }}
          options={filtersData.assignmentPartners}
          value={filters.assignmentPartners}
          hideCheckBox
          handleChange={setAssignmentPartners}
          sx={{ width: '15ch' }}
          selectProps={{ displayEmpty: true }}
        />

        <Box pr={2} />
        <Divider orientation="vertical" variant="middle" flexItem />
        <Box pr={2} />

        <ChoiceRecap
          id="total"
          title={t('assignment.car-assignment.labelTitle.allCars')}
          value={choices.total}
          handleClick={() =>
            setFilteringCaps(FILTER_ASSIGNMENT_CAPS_NONE_CHOICE)
          }
          border
        />
        <Box pr={1} />
        <ChoiceRecap
          id={'firstChoice'}
          title={t('assignment.car-assignment.labelTitle.firstChoice')}
          value={choices.firstChoice}
          handleClick={() =>
            setFilteringCaps(FILTER_ASSIGNMENT_CAPS_FIRST_CHOICE)
          }
          selected={
            filters.assignmentCaps === FILTER_ASSIGNMENT_CAPS_FIRST_CHOICE
          }
          border
        />
        <Box pr={1} />
        <ChoiceRecap
          id="blindPositiveOffers"
          title={t('assignment.car-assignment.labelTitle.blindPositiveOffers')}
          value={choices.blindPositiveOffers}
          handleClick={() =>
            setFilteringCaps(FILTER_ASSIGNMENT_CAPS_BLIND_POSITIVE_OFFERS)
          }
          selected={
            filters.assignmentCaps ===
              FILTER_ASSIGNMENT_CAPS_BLIND_POSITIVE_OFFERS ||
            filters.assignmentCaps === FILTER_ASSIGNMENT_CAPS_BLIND_OFFERS
          }
          border
        />
        <Box pr={1} />
        <ChoiceRecap
          id="blindNegativeOffers"
          title={t('assignment.car-assignment.labelTitle.blindNegativeOffers')}
          value={choices.blindNegativeOffers}
          handleClick={() =>
            setFilteringCaps(FILTER_ASSIGNMENT_CAPS_BLIND_NEGATIVE_OFFERS)
          }
          selected={
            filters.assignmentCaps ===
            FILTER_ASSIGNMENT_CAPS_BLIND_NEGATIVE_OFFERS
          }
          border
        />
        <Box pr={1} />
        <ChoiceRecap
          id="notChosen"
          title={t('assignment.car-assignment.labelTitle.notChosen')}
          value={choices.notChosen}
          handleClick={() =>
            setFilteringCaps(FILTER_ASSIGNMENT_CAPS_NOT_CHOSEN)
          }
          selected={
            filters.assignmentCaps === FILTER_ASSIGNMENT_CAPS_NOT_CHOSEN
          }
          border
        />

        <Box pr={2} />
        <Divider orientation="vertical" variant="middle" flexItem />
        <Box pr={2} />

        <ChoiceRecap
          id="allCars"
          title={t('assignment.car-assignment.labelTitle.allCars')}
          value={choices.total}
          handleClick={() =>
            setAssignmentType(FILTER_ASSIGNMENT_TYPE_NONE_CHOICE)
          }
          border
        />
        <Box pr={1} />
        <ChoiceRecap
          id="automatic"
          title={t('assignment.car-assignment.labelTitle.automatic')}
          value={choices.automatic}
          handleClick={() =>
            setAssignmentType(FILTER_ASSIGNMENT_TYPE_AUTOMATIC_CHOICE)
          }
          selected={
            filters.assignmentType === FILTER_ASSIGNMENT_TYPE_AUTOMATIC_CHOICE
          }
          border
        />
        <Box pr={1} />
        <ChoiceRecap
          id="teamProposal"
          title={t('assignment.car-assignment.labelTitle.teamProposal')}
          value={choices.teamProposal}
          handleClick={() =>
            setAssignmentType(FILTER_ASSIGNMENT_TYPE_TEAM_PROPOSAL_CHOICE)
          }
          selected={
            filters.assignmentType ===
            FILTER_ASSIGNMENT_TYPE_TEAM_PROPOSAL_CHOICE
          }
          border
        />
        <Box pr={1} />
        <ChoiceRecap
          id="noProposal"
          title={t('assignment.car-assignment.labelTitle.noProposal')}
          value={choices.noProposal}
          valueProps={{ color: choices.noProposal ? 'error' : undefined }}
          handleClick={() =>
            setAssignmentType(FILTER_ASSIGNMENT_TYPE_NO_PROPOSAL_CHOICE)
          }
          selected={
            filters.assignmentType === FILTER_ASSIGNMENT_TYPE_NO_PROPOSAL_CHOICE
          }
          border
        />
        <Box pr={1} />
        {showAppealing && (
          <>
            <ChoiceRecap
              id="appealing"
              title={t('assignment.car-assignment.labelTitle.appealing')}
              value={choices.appealing ? `${choices.appealing}/7` : '-'}
              border
            />
            <Box pr={1} />
          </>
        )}
      </Stack>
    </>
  )
}

AssignmentChoicesRecap.propTypes = {
  cars: T.array,
  setFilteringCaps: T.func,
  setAssignmentType: T.func,
  showAppealing: T.bool,
  readOnly: T.bool,
}
